// @ts-strict-ignore
import {Injectable, NgZone, OnDestroy} from "@angular/core";
import {ClientType, Device, HealthErrorCodes, ServiceType, TrainingRoom, WebAudioClientMapping} from "@models";
import {TranslateService} from "@ngx-translate/core";
import {Translations} from "@translations";
import {AudioHealthResult, SimSharedAudioDataService} from "./sim-shared-audio-data.service";
import {SimConfigurationService} from "./sim-configuration.service";
import {SimUtilityService} from "./sim-utility.service";
import {GlobalEventEmitterService} from "@event-emitter";
import {environment} from "../../../../environments/environment";
import {HealthErrorLocation, SimHealthMonitorExtendedService} from "./sim-service-health-monitor.service";

@Injectable()
export class WebsocketAudioClient implements OnDestroy {
	T = Translations;
	private audioConnection: AudioConnection = new AudioConnection();

	private audioBufferSize: number;
	private websocketAudioEndpoint: string;

	private audioContextOut: AudioContext = null;
	private websocketWorker: Worker = null;

	private audioScriptProcessorBufferSize = 0;
	private audioScriptProcessorOutputBuffer: AudioBufferSourceNode = null;
	private audioDynamicsCompressorNode: DynamicsCompressorNode = null;
	private audioGainNode: GainNode = null;

	private audioWorkletNode: AudioWorkletNode = null;
	private audioWorkletStarting = false;
	private audioWorkletStarted = false;
	private audioWorkletModuleAdded = false;

	private audioStatisticsData: any = null;
	private audioStatisticsDataLastUpdateTime = 0;
	private audioStatisticsDataLastWorkerCounter = 0;
	private audioStatisticsDataLastProcessorCounter = 0;
	private audioStatisticsDataLastStatisticsCounter = 0;
	private audioState: "slow-network" | "slow-cpu" | "stable" | "undefined" = "undefined";

	private destroyed = false;
	private destroying = false;

	private enableReconnect = false;
	private readonly enablePreemptiveHealthCheck = true;

	private reconnectIntervalHandler: any = null;
	private resumeAudioContextIntervalHandler: any = null;
	private reconnectCoolDown = 0;

	private audioOutputMuted = false;
	private audioOutputVolume = 100;

	private audioMuted = false;
	private audioVolumeMultiplier = 1.0;
	private audioVolumeMax = 120;
	private requestVolumeData = true;
	private requestLatencyData = false;
	private trainingRoomId: string = null;
	private announcementSessionId: string = null;

	private webClientType: ClientType = ClientType.Undefined;
	private webClientMasterId: string = null;
	private webClient: WebAudioClientMapping | Device = null;

	private preferredFrameSize: number = null;
	private ringBufferLength: number = null;

	constructor(
		private _healthMonitorService: SimHealthMonitorExtendedService,
		private _emitterService: GlobalEventEmitterService,
		private _simSharedAudioDataService: SimSharedAudioDataService,
		private translate: TranslateService,
		private _zone: NgZone,
	) {
		this.initializeReconnect();
	}

	async ngOnDestroy() {
		console.log("WebsocketAudioClient destroyed");
		await this.destroy();
	}

	private initializeReconnect() {
		if (this.reconnectIntervalHandler) {
			clearInterval(this.reconnectIntervalHandler);
		}

		this._zone.runOutsideAngular(() => {
			this.reconnectIntervalHandler = setInterval(async () => {
				if (this.enableReconnect) {
					if (this.audioConnection.connectionState !== "opened" && !this.audioConnection.isConnecting) {
						if (this.reconnectCoolDown <= 0) {
							await this._zone.run(async () => {
								let result = await this.initializeAudioInternal();
								if (result?.success) {
									this._emitterService.onAudioReconnectedSuccessfully.next();
								} else {
									this._emitterService.onAudioReconnectionFailed.next();
								}
							});
						} else if (this.reconnectCoolDown > 0) {
							this.reconnectCoolDown--;
						}
					} else {
						//already connected or connection in progress
					}
				} else {
					//automatic reconnection disabled
				}
			}, 3000);
		});
	}

	private initializeResumeAudioContext() {
		if (this.resumeAudioContextIntervalHandler) {
			clearInterval(this.resumeAudioContextIntervalHandler);
		}

		this._zone.runOutsideAngular(() => {
			this.resumeAudioContextIntervalHandler = setInterval(async () => {
				try {
					if (this.audioContextOut && this.audioContextOut.state === "suspended") {
						console.debug("Resuming audio context");
						await this.audioContextOut.resume();
					}
				} catch (e) {
					console.error("Error while resuming audio context", e);
				}
			}, 3000);
		});
	}

	/**
	 * Starts the audio streaming over websocket
	 */
	public async initializeAudio(
		websocketAudioEndpoint: string,
		audioBufferSize: number,
		webClientType: ClientType,
		webClientMasterId: string,
		webClient: WebAudioClientMapping | Device,
		preferredFrameSize: number,
		ringBufferLength: number,
		audioMuted: boolean,
		audioVolumeMultiplier: number,
		audioVolumeMax: number,
		audioOutputVolume: number,
		audioOutputMuted: boolean,
		requestVolumeData: boolean,
		requestLatencyData: boolean,
		trainingRoomId: string,
		announcementSessionId: string,
	): Promise<InitializeAudioResult> {
		this.websocketAudioEndpoint = websocketAudioEndpoint;
		this.audioBufferSize = audioBufferSize;
		this.webClientType = webClientType;
		this.webClientMasterId = webClientMasterId;
		this.webClient = webClient;
		this.preferredFrameSize = preferredFrameSize;
		this.ringBufferLength = ringBufferLength;
		this.audioMuted = audioMuted;
		this.audioVolumeMultiplier = audioVolumeMultiplier;
		this.audioVolumeMax = audioVolumeMax;
		this.audioOutputVolume = audioOutputVolume;
		this.audioOutputMuted = audioOutputMuted;
		this.requestVolumeData = requestVolumeData;
		this.requestLatencyData = requestLatencyData;
		this.trainingRoomId = trainingRoomId;
		this.announcementSessionId = announcementSessionId;

		return await this.initializeAudioInternal();
	}

	private async initializeAudioInternal(): Promise<InitializeAudioResult> {
		this.destroyAudioWorklet();

		this.audioConnection.isConnecting = true;
		this.enableReconnect = true;

		try {
			//console.info("webClientMasterId: " + this.webClientMasterId);
			if (this.audioMuted) {
				console.info("WebSocket Audio muted!");
			}

			let healthResult = new AudioHealthResult();
			if (!this.destroyed && !this.destroying) {
				if (this.websocketAudioEndpoint) {
					if (this.enablePreemptiveHealthCheck) {
						healthResult = await this.checkAudioServerHealth();
					}
				} else {
					healthResult.errorMessage = this.T.WEB_RECORDER_AUDIO_SERVER_IP_MISSING;
					this._simSharedAudioDataService.audioHealth.set(healthResult);

					// No audio endpoint configured, valid use case.
					return {
						success: true,
						reason: null,
					};
				}
			} else {
				throw new Error(this.translate.instant(this.T.WEB_RECORDER_AUDIO_SERVER_INSTANCE_ALREADY_DESTROYED));
			}

			if (!this.destroyed && !this.destroying) {
				if (healthResult.isHealthy) {
					// See: SIMSOFT-4062
					// On some devices the SharedArrayBuffer is not activated, but we
					// need it to transport audio data from the websocket-worker to the web-audio-worklet.
					if (typeof window["SharedArrayBuffer"] === "function") {
						if (!this.audioContextOut) {
							// 2020-02-20, Alex
							// See: SIMSOFT-4813
							// Now chrome can change the sample rate:
							// https://www.chromestatus.com/feature/5136778254090240
							this.audioContextOut = new AudioContext({
								sampleRate: SimConfigurationService.settings.audioServerSampleRate,
								latencyHint: "interactive",
							});

							// --------------------------------------------------------------------- //
							// This is a workaround for the Chrome issue, that the audio output stream
							// is closed after ~40sec of inactivity. That results in a few 100ms of
							// audio being cut off. This workaround is not perfect, but it works.
							// --------------------------------------------------------------------- //

							// Create a gain node with zero gain
							const gainNode = this.audioContextOut.createGain();
							gainNode.gain.value = 0.000000001;

							// Create an oscillator node
							const oscillator = this.audioContextOut.createOscillator();
							oscillator.frequency.value = 20000; // The frequency doesn't really matter
							oscillator.connect(gainNode).connect(this.audioContextOut.destination);

							// Start the oscillator
							oscillator.start();

							// Because gain = 0, you won't hear anything,
							// but the context will continue to “see” active playback.
						}

						if (this.audioContextOut.state !== "suspended") {
							if (!this.websocketWorker) {
								console.warn("Creating Websocket-Worker ...");

								this.websocketWorker = new Worker(new URL("./workers/audio-websocket.worker.ts", import.meta.url), {
									type: "module",
								});
							}

							this.websocketWorker.onerror = (err) => {
								console.warn("WebSocket Audio Worker Error: " + err?.message, err?.error);
								this.audioConnection.isConnecting = false;
							};

							this._zone.runOutsideAngular(() => {
								this.websocketWorker.onmessage = (ev: MessageEvent) => {
									if (ev && ev.data) {
										const message = ev.data;

										switch (message.topic) {
											case "audio-open":
												this._zone.run(() => {
													this.audioConnection.connectionState = "opened";
													this.audioConnection.isConnecting = false;
												});
												break;
											case "audio-closed":
												this._zone.run(() => {
													this.audioConnection.connectionState = "closed";
													this.audioConnection.isConnecting = false;
													this.destroyAudioWorklet();

													const result = new AudioHealthResult();
													result.errorMessage = this.T.WEB_RECORDER_AUDIO_CONNECTION_CLOSED;
													this._simSharedAudioDataService.audioHealth.set(result);

													if (message.reason === 1006) {
														// this._simSharedAudioDataService.audioError = this.translate.instant(this.T.WEB_RECORDER_AUDIO_CONNECTION_CLOSED_DUE_TO_SLOWNESS);

														// 2019-07-04, Alex
														// See: SIMSOFT-3251
														// Don't try to reconnect immediately after error. This leads to flickering ui.
														this.reconnectCoolDown = 5;
													}
												});
												break;
											case "audio-error":
												this._zone.run(() => {
													this.audioConnection.connectionState = "closed";
													this.audioConnection.isConnecting = false;
													this.destroyAudioWorklet();

													const messageError = this.translate.instant(this.T.WEB_RECORDER_AUDIO_CON_FAILED);
													const messageEndpoint = this.translate.instant(this.T.WEB_RECORDER_AUDIO_ENDPOINT);

													const result = new AudioHealthResult();
													result.errorMessage = `${messageError} (${messageEndpoint}: ${this.websocketAudioEndpoint})`;
													this._simSharedAudioDataService.audioHealth.set(result);
												});
												break;
											case "websocket-initialized":
												console.log("Websocket was initialized: " + message.data.id);
												break;
											case "slow-incoming-audio-data":
												const deviationInPercentSlower =
													((message.bitrate - message.optimalBitrate) / message.optimalBitrate) * 100;

												console.warn(
													`slow-incoming-audio-data: ${message.bitrate} < ${message.optimalBitrate} => ${deviationInPercentSlower}%`,
												);
												break;
											case "too-fast-incoming-audio-data":
												const deviationInPercentFaster =
													((message.bitrate - message.optimalBitrate) / message.optimalBitrate) * 100;

												console.warn(
													`too-fast-incoming-audio-data: ${message.bitrate} > ${message.optimalBitrate} => ${deviationInPercentFaster}%`,
												);
												break;
											case "websocket-session-id-received":
												this.audioConnection.websocketSessionId = message.data.websocketSessionId;
												break;
											case "frame-size-received":
												// Performance optimization. We don't need to reinitialize the audio, if the
												// frame size does not change. The audio server sends a "frame-size-received", after
												// every room change.
												this.startAudioWorkletProcessor(message.data.frameSize, message.data.sharedBuffer);
												break;
											case "latency-received":
												this.audioConnection.latency = Math.max(Math.min(message.data.latency, 2000), 0);
												break;
											case "volumes-received":
												this._zone.run(() => {
													this._simSharedAudioDataService.volumeData.set(message.data);
												});
												break;
										}
									}
								};
							});

							this.websocketWorker.postMessage({
								name: "mute",
								parameters: {
									audioMuted: this.audioMuted,
								},
							});

							this.websocketWorker.postMessage({
								name: "start",
								parameters: {
									host: this.websocketAudioEndpoint,
									webClientType: this.webClientType,
									webClientMasterId: this.webClientMasterId,
									webClient: this.webClient,
									preferredFrameSize: this.preferredFrameSize,
									ringBufferLength: this.ringBufferLength,
									audioVolumeMultiplier: this.audioVolumeMultiplier,
									audioVolumeMax: this.audioVolumeMax,
									sampleRate: SimConfigurationService.settings.audioServerSampleRate,
									requestVolumeData: this.requestVolumeData,
									requestLatencyData: this.requestLatencyData,
									trainingRoomId: this.trainingRoomId,
									announcementSessionId: this.announcementSessionId,
								},
							});

							this.initializeResumeAudioContext();

							return {
								success: true,
								reason: null,
							};
						} else {
							this.enableReconnect = false;

							this._emitterService.onAudioNeedsUserConfirmation.next();

							throw new Error(this.translate.instant(this.T.WEB_RECORDER_AUDIO_SUSPENDED));
						}
					} else {
						this.enableReconnect = false;

						throw new Error(this.translate.instant(this.T.WEB_RECORDER_AUDIO_SHARED_BUFFER_NOT_AVAILABLE));
					}
				} else {
					throw healthResult.errorMessage;
				}
			} else {
				throw new Error("Already destroyed");
			}
		} catch (ex) {
			this.audioConnection.isConnecting = false;
			return {
				success: false,
				reason: this.translate.instant(ex?.message ?? ex),
			};
		}
	}

	private async checkAudioServerHealth(): Promise<AudioHealthResult> {
		let result = new AudioHealthResult();

		this.audioConnection.lastErrorCodes = [];
		const systemStatus = await this._healthMonitorService.getSystemStatus().catch(() => {
			result.errorMessage = this.translate.instant(this.T.WEB_RECORDER_AUDIO_HEALTH_CHECK_FAILED);
		});

		if (systemStatus) {
			const allErrorCodes =
				systemStatus.components?.find((x) => x.serviceType == ServiceType.SimAudioServer)?.status?.errorCodes || [];

			const criticalErrorCodes =
				this._healthMonitorService
					.getComponentErrors(systemStatus, {include: HealthErrorLocation.StartRecording})
					?.find((x) => x.serviceType == ServiceType.SimAudioServer)?.errorCodes || [];
			const warningErrorCodes = allErrorCodes?.filter((x) => criticalErrorCodes?.indexOf(x) < 0) || [];

			this.audioConnection.lastErrorCodes = allErrorCodes;
			result.setErrorCodes(criticalErrorCodes, warningErrorCodes);
		}

		this._simSharedAudioDataService.audioHealth.set(result);
		return result;
	}

	// 2019-04-03, Alex
	// We have to prevent the second initialization of the web audio worklet
	// otherwise the two audio worklets will read twice as fast which will
	// lead to "robotic sound".
	// @TODO: We have to check if audio playback is started again, while already running.
	private async startAudioWorkletProcessor(frameBufferSize: number, sharedBuffer) {
		let bufferSizeUnchanged = true;
		if (this.audioScriptProcessorBufferSize !== frameBufferSize) {
			this.destroyAudioWorklet();
			this.audioScriptProcessorBufferSize = frameBufferSize;
			bufferSizeUnchanged = false;
		}

		if (!this.audioWorkletStarted) {
			if (!this.audioWorkletNode) {
				if (this.audioContextOut.state === "running") {
					if (this.audioContextOut.audioWorklet && typeof this.audioContextOut.audioWorklet.addModule === "function") {
						if (!this.audioWorkletStarting) {
							this.audioWorkletStarting = true;

							this.audioScriptProcessorOutputBuffer = this.audioContextOut.createBufferSource();
							this.audioDynamicsCompressorNode = this.audioContextOut.createDynamicsCompressor();
							this.audioGainNode = this.audioContextOut.createGain();

							this.updateAudioOutputGain();

							// --------------------------------------------------------------------- //
							// We have to prevent the second the loading of the module, otherwise    //
							// we get the problem that the audio floats are consumed twice.          //
							if (!this.audioWorkletModuleAdded) {
								await this.audioContextOut.audioWorklet
									.addModule(environment.baseHref + "/worklets/audio-worklet-fifo-processor-module.js")
									.catch((error) => console.error(error));

								this.audioWorkletModuleAdded = true;
							}
							// --------------------------------------------------------------------- //

							console.debug("Audio-Worklet added.");

							this.audioWorkletNode = new AudioWorkletNode(this.audioContextOut, "audio-worklet-fifo-processor", {
								processorOptions: {
									inputSize: this.audioScriptProcessorBufferSize || this.audioBufferSize || 1024,
									outputSize: 128,

									// Threshold for taking action 80 * 128 float samples => ~20% of 48000
									readWriteDistanceThreshold: SimConfigurationService.settings.audioReadWriteDistanceThreshold,

									// This skip 40 * 128 float samples = ‭5.120‬ => ~ 10% of 48000
									// Used when worker is slower than the processor
									coolDown: SimConfigurationService.settings.audioCoolDown,

									// Used when worker is faster than the processor
									heatUp: SimConfigurationService.settings.audioHeatUp,
								},
							});

							this.audioWorkletNode.port.onmessage = this.handleWorkletMessage.bind(this);

							this.audioWorkletNode.port.postMessage({
								message: "shared-buffer-received",
								sharedBuffer: sharedBuffer,
							});

							this.audioScriptProcessorOutputBuffer.connect(this.audioWorkletNode);

							this.audioWorkletNode.connect(this.audioGainNode);

							this.audioGainNode.connect(this.audioDynamicsCompressorNode);

							this.audioDynamicsCompressorNode.connect(this.audioContextOut.destination);

							// See: https://developers.google.com/web/updates/2017/09/autoplay-policy-changes#webaudio
							if (this.audioContextOut.state === "running") {
								this.audioScriptProcessorOutputBuffer.start();

								this.audioWorkletStarted = true;
								this.audioWorkletStarting = false;
							} else {
								this._emitterService.onAudioNeedsUserConfirmation.next();

								this.audioWorkletStarting = false;
							}
						} else {
							// Already starting
						}
					} else {
						this._emitterService.onAudioPlaybackNeedsSecureContext.next();
					}
				} else {
					this._emitterService.onAudioNeedsUserConfirmation.next();
				}
			} else {
				// Audio worklet node already build.
			}
		} else {
			// Audio worklet already started.
		}
	}

	private handleWorkletMessage(event) {
		const currentStatisticsData: any = event.data;
		const audioThreshold = 4800 * 2;
		let currentAudioStatus: "slow-network" | "slow-cpu" | "stable" | "undefined" = "undefined";

		if (currentStatisticsData) {
			if (this.audioStatisticsData) {
				if (this.audioStatisticsData["OB_FLOATS_DIFF"] > currentStatisticsData["OB_FLOATS_DIFF"] + audioThreshold) {
					currentAudioStatus = "slow-network";
					// console.log("Network is slow. Audio data is transferred slower than it is read.");
				} else if (this.audioStatisticsData["OB_FLOATS_DIFF"] < currentStatisticsData["OB_FLOATS_DIFF"] - audioThreshold) {
					currentAudioStatus = "slow-cpu";
					// console.log("CPU is slow. Audio data is read slower than it is produced by the audio worker.");
				} else {
					currentAudioStatus = "stable";
					// console.log("Audio stream is stable.");
				}

				const currentUpdateTime = performance.now();
				const currentWorkerCounter = this.audioStatisticsData["WORKER_COUNTER"];
				const currentProcessorCounter = this.audioStatisticsData["PROCESSOR_COUNTER"];
				const currentStatisticsCounter = this.audioStatisticsData["STATISTICS_COUNTER"];
				const currentNoDataCounter = this.audioStatisticsData["NO_DATA_COUNTER"];
				const currentSkipCounter = this.audioStatisticsData["SKIP_COUNTER"];
				const currentReadDistance = this.audioStatisticsData["READ_DISTANCE"];

				const updateTimeDiff = currentUpdateTime - this.audioStatisticsDataLastUpdateTime;
				const workerCounterDiff = currentWorkerCounter - this.audioStatisticsDataLastWorkerCounter;
				const processorCounterDiff = currentProcessorCounter - this.audioStatisticsDataLastProcessorCounter;
				const statisticsCounterDiff = currentStatisticsCounter - this.audioStatisticsDataLastStatisticsCounter;

				console.debug(
					"Elapsed ms: " +
						updateTimeDiff +
						", " +
						"Worker Counter: " +
						currentWorkerCounter +
						", " +
						"Worker Counter Diff: " +
						workerCounterDiff +
						", " +
						"Processor Diff: " +
						processorCounterDiff +
						", " +
						"No Data Counter: " +
						currentNoDataCounter +
						", " +
						"Skip Counter: " +
						currentSkipCounter +
						", " +
						"Statistics Diff: " +
						statisticsCounterDiff +
						", " +
						"Read Distance: " +
						currentReadDistance,
				);

				this.audioStatisticsDataLastUpdateTime = performance.now();
				this.audioStatisticsDataLastWorkerCounter = currentWorkerCounter;
				this.audioStatisticsDataLastProcessorCounter = currentProcessorCounter;

				if (this.audioState !== currentAudioStatus) {
					this.audioState = currentAudioStatus;

					this._emitterService.onAudioStateChanged.next({
						audioState: this.audioState,
					});
				}
			}

			this.audioStatisticsData = currentStatisticsData;
		}
	}

	public async resumeAudio(): Promise<boolean> {
		if (this.audioContextOut) {
			await this.audioContextOut.resume();
			let result = await this.initializeAudioInternal();
			if (result?.success) {
				this._emitterService.onAudioReconnectedSuccessfully.next();
				return true;
			} else {
				this._emitterService.onAudioReconnectionFailed.next();
				return false;
			}
		} else {
			return false;
		}
	}

	/*#####################################################
                CHANGE VOLUME
    ####################################################### */

	public changeAudioMix(trainingRoom: TrainingRoom) {
		this.trainingRoomId = trainingRoom.id;
	}

	public setAudioOutputVolume(volume: number) {
		this.audioOutputVolume = volume;

		this.updateAudioOutputGain();
	}

	public setAudioOutputMuted(muted: boolean) {
		this.audioOutputMuted = muted;

		this.updateAudioOutputGain();
	}

	private updateAudioOutputGain() {
		if (this.audioConnection && this.audioGainNode) {
			if (this.audioOutputMuted) {
				SimUtilityService.setGainNodeValue(this.audioGainNode, 0);
			} else {
				SimUtilityService.setGainNodeValue(this.audioGainNode, this.audioOutputVolume);
			}
		}
	}

	/*#####################################################
            DESTROY
    ####################################################### */

	private destroyAudioWorklet() {
		if (this.audioScriptProcessorOutputBuffer) {
			// See: SIMSOFT-3867
			try {
				this.audioScriptProcessorOutputBuffer.stop();
				this.audioScriptProcessorOutputBuffer.disconnect(this.audioWorkletNode);
			} catch (ex) {
				console.warn(ex);
			}

			this.audioScriptProcessorOutputBuffer = null;
		}

		if (this.audioWorkletNode) {
			try {
				this.audioWorkletNode.disconnect(this.audioGainNode);
				this.audioWorkletNode.port.postMessage({
					message: "stop",
				});
				this.audioWorkletNode.port.close();
			} catch (ex) {
				console.warn(ex);
			}

			this.audioWorkletNode = null;
		}

		if (this.audioGainNode) {
			try {
				this.audioGainNode.disconnect(this.audioDynamicsCompressorNode);
			} catch (ex) {
				console.warn(ex);
			}

			this.audioGainNode = null;
		}

		if (this.audioDynamicsCompressorNode) {
			try {
				this.audioDynamicsCompressorNode.disconnect(this.audioContextOut.destination);
			} catch (ex) {
				console.warn(ex);
			}

			this.audioDynamicsCompressorNode = null;
		}

		// Reset all letiables
		this.audioWorkletStarted = false;
	}

	public async destroy() {
		this.enableReconnect = false;
		this.destroying = true;

		if (this.reconnectIntervalHandler) {
			clearInterval(this.reconnectIntervalHandler);
		}

		if (this.resumeAudioContextIntervalHandler) {
			clearInterval(this.resumeAudioContextIntervalHandler);
		}

		if (this.websocketWorker) {
			this.websocketWorker.postMessage({
				name: "stop",
			});

			this.websocketWorker.terminate();
			this.websocketWorker = null;
		}

		this.destroyAudioWorklet();

		// 7) deallocate audio context
		if (this.audioContextOut && this.audioContextOut.state !== "closed") {
			await this.audioContextOut.close();
			this.audioContextOut = null;

			// If we reset the audio worklet, all added modules disappear.
			this.audioWorkletModuleAdded = false;
		}

		this.destroyed = true;
	}
}

class AudioConnection {
	public websocketSessionId: string = null;
	public latency = 0;
	public connectionState: "opened" | "closed" = "closed";
	public isConnecting = false;
	public lastErrorCodes: HealthErrorCodes[] = null;
}

interface InitializeAudioResult {
	success: boolean;
	reason: number | string;
}
